import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';

@Component({
  selector: 'aid-date-picker',
  template: `
    <mat-form-field color="primary" class="full-width">
      @if (label) {
        <mat-label>
          {{ label | translate }}
        </mat-label>
      }
      @if (!isRange) {
        <input
          matInput
          [matDatepicker]="picker"
          [formControl]="dateControl"
          [required]="isRequired"
          [min]="minDate"
          [max]="maxDate"
          [placeholder]="'date-placeholder' | translate"
          data-cy="reminder-date-picker2"
          data-cy="aid-date-picker"
        />
      }

      @if (isRange) {
        <mat-date-range-input
          [rangePicker]="rangePicker"
          [formGroup]="rangeGroup"
        >
          <input
            matStartDate
            placeholder="{{ 'start-date-placeholder' | translate }}"
            formControlName="from"
          />
          <input
            matEndDate
            placeholder="{{ 'end-date-placeholder' | translate }}"
            formControlName="to"
          />
        </mat-date-range-input>
      }

      @if (!isRange) {
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
      }
      @if (isRange) {
        <mat-datepicker-toggle
          matSuffix
          [for]="rangePicker"
        ></mat-datepicker-toggle>
      }

      <mat-date-range-picker #rangePicker></mat-date-range-picker>
      <mat-datepicker #picker></mat-datepicker>

      @if (control.hasError('required')) {
        <mat-error>
          {{ 'date' | translate | titlecase }} {{ 'is-required' | translate }}
        </mat-error>
      }
      @if (control.hasError('endDate')) {
        <mat-error>
          {{ 'end-date-error' | translate | sentancecase }}
        </mat-error>
      }
      @if (control.hasError('startDate')) {
        <mat-error>
          {{ 'start-date-error' | translate | sentancecase }}
        </mat-error>
      }

      @if (control.hasError('matDatepickerMin')) {
        <mat-error>
          {{
            'date-min-error'
              | translate: { date: minDate | date: 'shortDate' }
              | sentancecase
          }}
        </mat-error>
      }
      @if (control.hasError('matDatepickerParse')) {
        <mat-error>
          {{ 'date-invalid' | translate | sentancecase }}
        </mat-error>
      }

      @if (control.hasError('matDatepickerMax')) {
        <mat-error>
          {{
            'date-max-error'
              | translate: { date: maxDate | date: 'shortDate' }
              | sentancecase
          }}
        </mat-error>
      }
      @for (error of control.errors | keyvalue; track error) {
        <mat-error>
          @if (error.value && displayError(error.key)) {
            {{ error.value | translate }}
          }
        </mat-error>
      }
    </mat-form-field>
  `,
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DatePickerComponent implements OnInit {
  @Input({ required: true }) control: UntypedFormControl | UntypedFormGroup =
    new UntypedFormControl();
  @Input() minDate?: Date | null;
  @Input() maxDate?: Date | null;
  @Input() label?: string;
  @Input() isRange: boolean = false;

  constructor() {}

  ngOnInit() {}

  get dateControl(): UntypedFormControl {
    if (!(this.control instanceof UntypedFormControl)) {
      throw new Error('aid-date-picker: a single date needs a FormControl.');
    }
    return this.control;
  }

  get rangeGroup(): UntypedFormGroup {
    if (!(this.control instanceof UntypedFormGroup)) {
      throw new Error('aid-date-picker: a date range needs a FormGroup.');
    }
    return this.control;
  }

  get isRequired(): boolean {
    if (this.control.validator) {
      const validator = this.control.validator({} as AbstractControl);
      if (validator && validator.required) {
        return true;
      }
    }

    return false;
  }

  /**
   * TODO Refactor all the validators to send the error message on control
   * Remove this function when all the custom errors are done
   */
  displayError(error: string): boolean {
    return !(
      error === 'required' ||
      error === 'endDate' ||
      error === 'startDate' ||
      error === 'matDatepickerMin' ||
      error === 'matDatepickerMax' ||
      error === 'matDatepickerParse'
    );
  }
}
